import React from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { PublicLayout, AuthLayout, DashboardLayout } from "@/components/layout";
import ProtectedRoute from "./ProtectedRoute";
import PublicOnlyRoute from "./PublicOnlyRoute";

// Lazy load pages for better performance
const HomePage = React.lazy(() => import("@/pages/home/HomePage"));
const SubscribePage = React.lazy(
  () => import("@/pages/subscribe/SubscribePage")
);
const WorkbenchPage = React.lazy(
  () => import("@/pages/workbench/WorkbenchPage")
);
const BlogPage = React.lazy(() => import("@/pages/blog/BlogPage"));
const FeedbackPage = React.lazy(
  () => import("@/pages/feedback/FeedbackListPage")
);
const FeedbackEditorPage = React.lazy(
  () => import("@/pages/feedback/FeedbackEditorPage")
);
const FeedbackDetailPage = React.lazy(
  () => import("@/pages/feedback/FeedbackDetailPage")
);

// Auth pages
const LoginPage = React.lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = React.lazy(() => import("@/pages/auth/RegisterPage"));
const VerifyEmailPage = React.lazy(
  () => import("@/pages/auth/VerifyEmailPage")
);
const ForgotPasswordPage = React.lazy(
  () => import("@/pages/auth/ForgotPasswordPage")
);
const ResetPasswordPage = React.lazy(
  () => import("@/pages/auth/ResetPasswordPage")
);

// Dashboard pages
const ApiKeysPage = React.lazy(() => import("@/pages/dashboard/ApiKeysPage"));
const SubscriptionPage = React.lazy(
  () => import("@/pages/dashboard/SubscriptionPage")
);
const SettingsPage = React.lazy(() => import("@/pages/dashboard/SettingsPage"));

export const router = createBrowserRouter([
  // Public routes
  {
    path: "/",
    element: <PublicLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: "subscribe",
        element: <SubscribePage />,
      },
      {
        path: "workbench",
        element: <WorkbenchPage />,
      },
      {
        path: "playground",
        element: <Navigate to="/workbench" replace />,
      },
      {
        path: "blog",
        element: <BlogPage />,
      },
      {
        path: "feedback",
        element: <FeedbackPage />,
      },
      {
        path: "feedback/new",
        element: <FeedbackEditorPage />,
      },
      {
        path: "feedback/:id",
        element: <FeedbackDetailPage />,
      },
    ],
  },

  // Auth routes
  {
    path: "/auth",
    element: <PublicOnlyRoute />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "login",
            element: <LoginPage />,
          },
          {
            path: "register",
            element: <RegisterPage />,
          },
          {
            path: "verify-email",
            element: <VerifyEmailPage />,
          },
          {
            path: "forgot-password",
            element: <ForgotPasswordPage />,
          },
          {
            path: "reset-password",
            element: <ResetPasswordPage />,
          },
        ]
      }
    ],
  },

  // Protected dashboard routes
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard/api-keys" replace />,
      },
      {
        path: "api-keys",
        element: <ApiKeysPage />,
      },
      {
        path: "subscription",
        element: <SubscriptionPage />,
      },
      {
        path: "settings",
        element: <SettingsPage />,
      },
    ],
  },

  // Catch all - 404
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);
